//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import * as ToggleGroup from '@radix-ui/react-toggle-group';
import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import { Mixins } from '@theme/mixins';
import { withTracking } from '@utils/withTracking';

interface Props<T> {
  children: ReactNode;
  onValueChange: (value: T) => void;
  className?: string;
  isDisabled?: boolean;
}

/**
 * `ToggleButtonGroup` is a component designed for selecting a single option from a group.
 * The entire group can be disabled to prevent user interaction, ensuring the component's
 * state remains consistent with the application's logic when necessary.
 *
 * @param {ReactNode} children - The toggle button options to be displayed within the group. Ensure that
 *                               `ToggleButton.Option` components are used to define these options.
 * @param {string} className - An optional CSS class for applying custom styles to the toggle button group.
 * @param {function} onValueChange - Callback function that is called when the selected value changes,
 *                                   providing a way to handle state updates and form submissions.
 * @param {boolean} [isDisabled] - Controls the enabled state of the entire toggle button group.
 *                                       Setting this to `true` disables all user interaction with the group.
 * @returns {React.ReactElement} A component that renders a group of toggle buttons, offering a visually
 *                               customizable and accessible selection interface for users.
 */
export const ToggleButtonGroup = <T extends string>({
  children,
  className,
  isDisabled,
  onValueChange,
}: Props<T>) => {
  return (
    <StyledToggleRoot
      className={className}
      disabled={isDisabled}
      onValueChange={onValueChange}
      type='single'
    >
      {children}
    </StyledToggleRoot>
  );
};

interface ToggleButtonGroupOptionProps {
  children: React.ReactNode;
  className?: string;
  isSelected: boolean;
  isDisabled?: boolean;
  trackingId?: string;
  value: string;
}

/**
 * `ToggleButtonGroup.Option` is tailored for inclusion within a `ToggleButtonGroup`, offering a selectable choice
 * in a set where only one option may be selected at any given time. This component aids in constructing
 * intuitive and accessible user interfaces by visually indicating its selection state, thereby enhancing
 * user experience through clear feedback.
 *
 * @param {React.ReactNode} children - The content displayed within the toggle button option, typically
 *                                     the label or title of the option.
 * @param {string} className - An optional CSS class for custom styling of the toggle button option.
 * @param {boolean} isSelected - Indicates whether the option is currently selected, controlling the
 *                               visual representation of the selection state.
 * @param {string} [trackingId] - An optional ID used for tracking user interactions with the option,
 *                                useful in analytics and event tracking scenarios.
 * @param {string} value - A unique value associated with the option, used to identify the selection
 *                         in form submissions and state management.
 * @returns A styled toggle button option component that can be interacted with.
 */
const ToggleButtonGroupOption = ({
  children,
  className,
  isSelected,
  isDisabled,
  trackingId,
  value,
}: ToggleButtonGroupOptionProps) => {
  return (
    <StyledToggleItem
      className={className}
      isSelected={isSelected}
      isDisabled={isDisabled}
      trackingId={trackingId}
      value={value}
    >
      {children}
    </StyledToggleItem>
  );
};

const StyledToggleItem = withTracking(
  styled(ToggleGroup.Item)<{ isSelected?: boolean; isDisabled?: boolean }>(
    ({ theme, isSelected, isDisabled }) => css`
      ${Mixins.Text.UiCopy}
      background-color: ${theme.colors.white};
      display: flex;
      align-items: center;
      justify-content: center;
      padding: ${theme.spacings[2]} ${theme.spacings[2]};
      border: ${theme.borderWidths[1]} solid ${theme.colors.gray2};
      border-right-width: ${theme.borderWidths[0]};

      &:first-child {
        border-top-left-radius: ${theme.borderRadii[2]};
        border-bottom-left-radius: ${theme.borderRadii[2]};
      }
      &:last-child {
        border-top-right-radius: ${theme.borderRadii[2]};
        border-bottom-right-radius: ${theme.borderRadii[2]};
        border-right-width: ${theme.borderWidths[1]};
      }

      ${isSelected
        ? css`
            color: ${theme.colors.teal1};
            font-weight: ${theme.fontWeights.bold};
            background-color: ${theme.colors.gray00};
          `
        : ''}

      ${isDisabled
        ? css`
            color: ${theme.colors.gray4};
            border-color: ${theme.colors.gray0};
            background-color: ${theme.colors.gray0};
          `
        : ''}
    `,
  ),
);

const StyledToggleRoot = styled(ToggleGroup.Root)(
  ({ theme }) => css`
    display: inline-flex;
    border-radius: ${theme.borderRadii[2]};
  `,
);

ToggleButtonGroup.Option = ToggleButtonGroupOption;
